import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

import { type UserType } from '../../../../services/types';
import { COLORS } from '../../../../utils/constants';

import './DonutChart.css';

interface DonutChartProps {
  data: UserType[];
  total: number;
  widthRem: number; // Width in rem
  heightRem: number; // Height in rem
}

function DonutChart({ data, total, widthRem, heightRem }: DonutChartProps) {
  const ref = useRef<SVGSVGElement | null>(null);
  const [tooltip, setTooltip] = useState<{
    type: string;
    count: number;
    percentage: string;
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    // Convert rem to pixels
    const baseFontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    ); // e.g., 16px
    const width = widthRem * baseFontSize;
    const height = heightRem * baseFontSize;

    const svg = d3.select(ref.current);

    svg.selectAll('*').remove(); // Clear previous render

    const radius = Math.min(width, height) / 2;

    const pie = d3.pie<UserType>().value((d) => d.count);
    const arc = d3
      .arc<d3.PieArcDatum<UserType>>()
      .innerRadius(radius * 0.68)
      .outerRadius(radius);

    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    // Draw paths for the donut chart
    const paths = g
      .selectAll('path')
      .data(pie(data))
      .enter()
      .append('path')
      .attr('d', arc as never)
      .attr('fill', (d) => d.data.color ?? COLORS[0])
      .attr('stroke', 'white')
      .attr('stroke-width', 2);

    // Handle hover effect
    paths
      .on('mouseover', function (event, d) {
        const [x, y] = d3.pointer(event); // Get cursor position relative to the SVG
        const percentage = `${((d.data.count / total) * 100).toFixed(2)}%`;

        setTooltip({
          type: d.data.type,
          count: d.data.count,
          percentage,
          x: x + width / 2 + 10, // Adjust X position with an offset
          y: y + height / 2 + 10, // Adjust Y position with an offset
        });
      })
      .on('mousemove', function (event) {
        const [x, y] = d3.pointer(event); // Update cursor position

        setTooltip((prev) =>
          prev
            ? {
                ...prev,
                x: x + width / 2 + 10, // Adjust X position with an offset
                y: y + height / 2 + 10, // Adjust Y position with an offset
              }
            : null
        );
      })
      .on('mouseout', () => {
        setTooltip(null); // Hide tooltip on mouseout
      });

    // Create legends (if needed, not related to cursor position)
  }, [data, total, widthRem, heightRem]);

  return (
    <div className='Ext-DonutChart'>
      <svg ref={ref} />
      {tooltip && (
        <div
          className='Ext-DonutChart-tootip'
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}>
          <strong>{tooltip.type}</strong>
          <br />
          Count: {tooltip.count}
          <br />
          Percentage: {tooltip.percentage}
        </div>
      )}
    </div>
  );
}

export default DonutChart;
